import React from 'react';
import { Pill } from 'lucide-react';
import type { Entry } from '../types';
import { canRecordEffect } from './EntryListItem';
import { entryTimeMs, latestEntryOfType, clockLabel } from '../model/entryMeta';
import { getEntryTitle } from '../utils';

/** Counts per entry type, most charted first (invalidated entries do not count) */
export const shiftCounts = (entries: Entry[]): { type: string; count: number }[] => {
  const counts = new Map<string, number>();
  for (const e of entries) if (e && !e.invalidated) counts.set(e.type, (counts.get(e.type) || 0) + 1);
  return Array.from(counts, ([type, count]) => ({ type, count })).sort((a, b) => b.count - a.count);
};

/** "58 min ago" / "2 h 5 min ago" ('' when the time cannot be read) */
export const agoLabel = (ms: number, now = Date.now()): string => {
  if (!ms) return '';
  const min = Math.max(0, Math.round((now - ms) / 60000));
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  return `${h} h${min % 60 ? ` ${min % 60} min` : ''} ago`;
};

const SHORT: Record<string, string> = { mar: 'Meds', vitals: 'Vitals', respiratory: 'Resp', notes: 'Notes', summary: 'Summary', measurements: 'Measurements' };

/**
 * Phase 18U U11: "This shift" — what has been charted, how long since the last vitals, and PRN
 * doses still waiting for their effect. Computed from the entries already loaded; no server call.
 */
const ShiftStrip: React.FC<{ entries: Entry[]; onRecordEffect?: (entry: Entry) => void; now?: number }> = ({ entries, onRecordEffect, now = Date.now() }) => {
  const counts = shiftCounts(entries);
  if (!counts.length) return null;
  const lastVitals = latestEntryOfType(entries, 'vitals');
  const prnDue = entries.filter((e) => canRecordEffect(e, entries));
  return (
    <section aria-label="This shift" className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="shift-strip">
      <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-700">This shift</h3>
      <p className="text-sm text-slate-900">
        {counts.slice(0, 5).map((c) => `${SHORT[c.type] || getEntryTitle(c.type)} ${c.count}`).join(' · ')}
      </p>
      <p className="mt-0.5 text-sm text-slate-700" data-testid="shift-last-vitals">
        {lastVitals ? `Last vitals ${agoLabel(entryTimeMs(lastVitals), now)}` : 'No vitals charted yet'}
      </p>
      {onRecordEffect && prnDue.map((e) => {
        const d = (e.data || {}) as { medicationName?: string };
        return (
          <button
            key={String(e.id)}
            type="button"
            onClick={() => onRecordEffect(e)}
            className="mt-2 flex min-h-[44px] w-full items-center gap-2 rounded-lg border border-green-400 bg-white px-3 text-left text-sm font-medium text-green-900"
          >
            <Pill size={16} aria-hidden="true" />
            Record effect: {d.medicationName || 'PRN dose'} given {clockLabel(e.observedAt || e.date)}
          </button>
        );
      })}
    </section>
  );
};

export default ShiftStrip;
